import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

export interface RuntimeConfig {
  apiBaseUrl: string;
  awsRegion: string;
  cognitoUserPoolId: string;
  cognitoUserPoolClientId: string;
  cognitoEndpoint: string;
  cognitoHostedUiDomain: string;
  xOAuthClientId: string;
  orcidEnabled: boolean;
}

let runtimeConfig: RuntimeConfig = {
  apiBaseUrl: environment.apiBaseUrl,
  awsRegion: environment.awsRegion,
  cognitoUserPoolId: '',
  cognitoUserPoolClientId: '',
  cognitoEndpoint: '',
  cognitoHostedUiDomain: '',
  xOAuthClientId: '',
  orcidEnabled: environment.name !== 'prod'
};

export async function loadRuntimeConfig(): Promise<void> {
  try {
    const response = await fetch('/config.json', { cache: 'no-store' });
    const contentType = response.headers.get('content-type') ?? '';
    if (!response.ok) throw new Error(`Unable to load runtime config: HTTP ${response.status}`);
    if (!contentType.includes('application/json')) throw new Error(`Runtime config is not JSON: ${contentType || 'missing content-type'}`);
    const config = await response.json() as Partial<RuntimeConfig>;
    const apiBaseUrl = normalizeApiBaseUrlForLocalDevelopment(config.apiBaseUrl || runtimeConfig.apiBaseUrl);
    runtimeConfig = {
      apiBaseUrl,
      awsRegion: config.awsRegion || runtimeConfig.awsRegion,
      cognitoUserPoolId: config.cognitoUserPoolId || '',
      cognitoUserPoolClientId: config.cognitoUserPoolClientId || '',
      cognitoEndpoint: config.cognitoEndpoint || '/api/auth/cognito',
      cognitoHostedUiDomain: config.cognitoHostedUiDomain || '',
      xOAuthClientId: config.xOAuthClientId || '',
      orcidEnabled: environment.name !== 'prod' && (config.orcidEnabled ?? runtimeConfig.orcidEnabled) === true
    };
  } catch (error) {
    console.warn('Runtime config could not be loaded. Cognito settings are unavailable until /config.json is available.', error);
  }
}

export function normalizeApiBaseUrlForLocalDevelopment(apiBaseUrl: string, currentLocation: Pick<Location, 'hostname' | 'origin'> = window.location): string {
  if (!apiBaseUrl) return apiBaseUrl;
  if (!['localhost', '127.0.0.1'].includes(currentLocation.hostname)) return apiBaseUrl;

  try {
    const configuredUrl = new URL(apiBaseUrl, currentLocation.origin);
    if (configuredUrl.hostname.endsWith('phdcompass.net') && configuredUrl.pathname.replace(/\/$/, '') === '/api') {
      return '/api';
    }
  } catch {
    return apiBaseUrl;
  }

  return apiBaseUrl;
}

@Injectable({ providedIn: 'root' })
export class RuntimeConfigService {
  get value(): RuntimeConfig { return runtimeConfig; }
  get apiBaseUrl(): string { return runtimeConfig.apiBaseUrl; }
  get awsRegion(): string { return runtimeConfig.awsRegion; }
  get cognitoUserPoolId(): string { return runtimeConfig.cognitoUserPoolId; }
  get cognitoUserPoolClientId(): string { return runtimeConfig.cognitoUserPoolClientId; }
  get cognitoEndpoint(): string { return runtimeConfig.cognitoEndpoint; }
  get cognitoHostedUiDomain(): string { return runtimeConfig.cognitoHostedUiDomain; }
  get xOAuthClientId(): string { return runtimeConfig.xOAuthClientId; }
  get orcidEnabled(): boolean { return runtimeConfig.orcidEnabled; }
}
